import {DowntimeStoppage} from '@domain/stoppages'
import {DataContentWrapper} from '@hconnect/common/containers'
import {formatNumber} from '@hconnect/common/utils'
import {dateFormatter} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {Typography} from '@mui/material'
import {TFunction} from 'i18next'
import moment from 'moment-timezone'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

const generateColumns = (
  t: TFunction,
  showDescriptions: boolean,
  language: string
): Column<DowntimeStoppage>[] => {
  const columns: Column<DowntimeStoppage>[] = [
    {
      key: 'stoppage-title',
      customTemplate: ({title}) => <Typography variant="subtitle1">{title}</Typography>,
      label: t('label.stoppageTitle')
    },
    {
      key: 'main-equipment',
      customTemplate: ({mainEquipment}) => (
        <Typography variant="subtitle1">{mainEquipment?.text ?? '-'}</Typography>
      ),
      label: t('label.mainEquipment')
    },
    {
      key: 'functionalLocation',
      customTemplate: ({equipment}) => (
        <Typography variant="subtitle1">{equipment?.text ?? '-'}</Typography>
      ),
      label: t('label.functionalLocation')
    },
    {
      key: 'stoppage-time',
      customTemplate: ({start, end}) => (
        <Typography variant="subtitle1">{`${dateFormatter(moment(start))} - ${dateFormatter(
          moment(end)
        )}`}</Typography>
      ),
      label: t('label.stoppageTime')
    },
    {
      key: 'stoppage-duration',
      customTemplate: ({start, end}) => (
        <Typography variant="subtitle1">
          {formatNumber(moment(end).diff(moment(start), 'hours', true), language)}
        </Typography>
      ),
      label: t('label.stoppageDuration')
    },
    {
      key: 'stoppage-rcfa',
      //   todo: this column will be available in future stories
      customTemplate: () => <Typography variant="subtitle1">-</Typography>,
      label: t('label.stoppageRcfa')
    }
  ]
  if (showDescriptions) {
    columns.push({
      key: 'stoppage-description',
      customTemplate: ({description}) => <Typography variant="subtitle1">{description}</Typography>,
      label: t('label.stoppageDescription')
    })
  }
  return columns
}

type Props = {
  stoppagesDetails: DowntimeStoppage[]
  isLoading: boolean
  refetch?: () => void
  isRefetching?: boolean
  isError: boolean
  showDescriptions: boolean
}

export const StoppageDetailsTable: React.FC<Props> = ({
  stoppagesDetails,
  isError,
  refetch,
  isRefetching,
  isLoading,
  showDescriptions
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)

  const columns = useMemo(
    () => generateColumns(t, showDescriptions, language),
    [t, showDescriptions, language]
  )

  return (
    <DataContentWrapper<DowntimeStoppage[]>
      showLoader={isLoading}
      data={stoppagesDetails}
      retryFunction={refetch}
      isError={isError}
      isRefetching={isRefetching}
      progressSx={{color: 'primary.main'}}
    >
      <DataTable<DowntimeStoppage>
        columns={columns}
        data={stoppagesDetails ?? []}
        data-test-id="details-table"
      />
    </DataContentWrapper>
  )
}
